import {
  Alert,
  Box,
  Button,
  Center,
  Collapse,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  Radio,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
  Badge,
} from '@mantine/core';
import { IconAlertTriangle, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import Router from 'next/router';
import { useState } from 'react';
import { AlertWithIcon } from '~/components/AlertWithIcon/AlertWithIcon';
import { useDialogContext } from '~/components/Dialog/DialogProvider';
import { dialogStore } from '~/components/Dialog/dialogStore';
import { EdgeMedia } from '~/components/EdgeMedia/EdgeMedia';
import { useMutatePaddle } from '~/components/Paddle/util';
import { useActiveSubscription } from '~/components/Stripe/memberships.util';
import { SubscribeButton } from '~/components/Stripe/SubscribeButton';
import { PlanBenefitList } from '~/components/Subscriptions/PlanBenefitList';
import type { PlanMeta } from '~/components/Subscriptions/getPlanDetails';
import { getPlanDetails } from '~/components/Subscriptions/getPlanDetails';
import { useTrackEvent } from '~/components/TrackView/track.utils';
import { useQueryVault, useQueryVaultItems } from '~/components/Vault/vault.util';
import { useFeatureFlags } from '~/providers/FeatureFlagsProvider';
import { PaymentProvider } from '~/shared/utils/prisma/enums';
import type { Price } from '~/shared/utils/prisma/models';
import { showErrorNotification, showSuccessNotification } from '~/utils/notifications';
import { formatKBytes } from '~/utils/number-helpers';
import { trpc } from '~/utils/trpc';
import { useAvailableBuzz } from '~/components/Buzz/useAvailableBuzz';
import { getSubscriptionDisplayBuzzType } from '~/shared/utils/buzz-membership';
import { useBuzzCurrencyConfig } from '~/components/Currency/useCurrencyConfig';
import { useReferralsContext } from '~/components/Referrals/ReferralsProvider';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { getClientStripe } from '~/utils/get-client-stripe';
import { OnboardingSteps } from '~/server/common/enums';
import { Flags } from '~/shared/utils/flags';

const downgradeReasons = ['Too expensive', 'I don’t need all the benefits', 'Others'];

export const DowngradeFeedbackModal = ({
  priceId,
  upcomingVaultSizeKb,
  fromTier,
  toTier,
}: {
  priceId: string;
  upcomingVaultSizeKb?: number;
  fromTier?: string;
  toTier?: string;
}) => {
  const dialog = useDialogContext();
  const handleClose = dialog.onClose;
  const [downgradeReason, setDowngradeReason] = useState('Others');
  const { vault, isLoading } = useQueryVault();
  const { trackAction } = useTrackEvent();

  const storageExceededAfterChange =
    upcomingVaultSizeKb && vault && upcomingVaultSizeKb < vault.usedStorageKb;

  return (
    <Modal {...dialog} size="md" title="Tell us why" radius="md">
      {isLoading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <Stack>
          <Radio.Group
            value={downgradeReason}
            label="Help us improve our services by leaving your feedback about the reason you want to downgrade."
            onChange={(value) => {
              setDowngradeReason(value);
            }}
            withAsterisk
          >
            <Stack gap="xs">
              {downgradeReasons.map((item) => (
                <Paper key={item} withBorder radius="md" p="md">
                  <Radio value={item} label={item} />
                </Paper>
              ))}
            </Stack>
          </Radio.Group>
          <AlertWithIcon color="red" icon={<IconAlertTriangle size={20} />} iconColor="red">
            <Stack>
              <Text>
                Downgrade is immediate and you will be charged instantly. You will lose your tier
                benefits as soon as you downgrade, and will receive the Buzz along the other
                benefits of the downgraded tier.
              </Text>
              <Text>
                If you have created{' '}
                <Text component="span" fw="bold">
                  private models
                </Text>{' '}
                during your time with your membership, you will have 10 days to make these public or
                download before the exceeding amount are deleted.
              </Text>
            </Stack>
          </AlertWithIcon>
          <Group grow>
            <SubscribeButton priceId={priceId} onSuccess={handleClose}>
              {({ onClick, ...props }) => (
                <Button
                  color="gray"
                  onClick={() => {
                    trackAction({
                      type: 'Membership_Downgrade',
                      details: {
                        reason: downgradeReason,
                        from: fromTier,
                        to: toTier,
                      },
                    }).catch(() => undefined);

                    if (storageExceededAfterChange) {
                      dialogStore.trigger({
                        component: VaultStorageDowngrade,
                        props: {
                          onContinue: () => {
                            onClick();
                          },
                        },
                      });
                    } else {
                      onClick();
                    }
                  }}
                  radius="xl"
                  {...props}
                >
                  Downgrade
                </Button>
              )}
            </SubscribeButton>
            <Button color="blue" onClick={handleClose} radius="xl">
              Don&rsquo;t change plan
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};

const cancelReasons = ['Too expensive', 'I found another service', 'I no longer need it', 'Others'];

export function CancelMembershipFeedbackModal() {
  const dialog = useDialogContext();
  const handleClose = dialog.onClose;
  const [cancelReason, setCancelReason] = useState('Others');
  const { isLoading } = useQueryVault();
  const { trackAction } = useTrackEvent();

  return (
    <Modal {...dialog} size="md" title="Tell us why" radius="md">
      {isLoading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <Stack>
          <Radio.Group
            value={cancelReason}
            label="Help us improve our service by leaving your feedback about the reason you wish to cancel"
            onChange={(value) => {
              setCancelReason(value);
            }}
            withAsterisk
          >
            <Stack gap="xs">
              {cancelReasons.map((item) => (
                <Paper key={item} withBorder radius="md" p="md">
                  <Radio value={item} label={item} />
                </Paper>
              ))}
            </Stack>
          </Radio.Group>
          <Group grow>
            <Button
              color="gray"
              onClick={() => {
                trackAction({
                  type: 'Membership_Cancel',
                  details: {
                    reason: cancelReason,
                    from: '',
                  },
                }).catch(() => undefined);

                handleClose();
                dialogStore.trigger({
                  component: CancelMembershipBenefitsModal,
                });
              }}
              radius="xl"
            >
              Continue
            </Button>
            <Button color="blue" onClick={handleClose} radius="xl">
              Don&rsquo;t cancel
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export const StripeCancelMembershipButton = ({
  onClose,
  hasUsedVaultStorage,
}: {
  onClose: () => void;
  hasUsedVaultStorage: boolean;
}) => {
  const { mutate, isPending: connectingToStripe } =
    trpc.stripe.cancelSubscriptionWithFallback.useMutation({
      async onSuccess(data) {
        if (data.type === 'redirect') {
          onClose();
          Router.push(data.url);
        } else {
          onClose();
          showSuccessNotification({
            title: 'Subscription cancelled',
            message: 'Your subscription has been cancelled successfully.',
          });
          window.location.reload();
        }
      },
      onError(error) {
        showErrorNotification({
          title: 'Failed to cancel subscription',
          error: new Error(error.message),
        });
      },
    });

  return (
    <Button
      color="gray"
      onClick={() => {
        if (hasUsedVaultStorage) {
          dialogStore.trigger({
            component: VaultStorageDowngrade,
            props: {
              onContinue: () => {
                mutate();
              },
            },
          });
        } else {
          mutate();
        }
      }}
      radius="xl"
      loading={connectingToStripe}
    >
      Proceed to Cancel
    </Button>
  );
};

export const PaddleCancelMembershipButton = ({
  onClose,
  hasUsedVaultStorage,
}: {
  onClose: () => void;
  hasUsedVaultStorage: boolean;
}) => {
  const { cancelSubscription, cancelingSubscription } = useMutatePaddle();
  const handleCancelSubscription = () => {
    cancelSubscription({
      onSuccess: (canceled) => {
        if (canceled) {
          onClose();
          showSuccessNotification({
            title: 'You have been successfully downgraded to our Free tier.',
            message: 'You will no longer be billed for your subscription',
          });
          window?.location.reload();
        }
      },
    });
  };

  return (
    <Button
      color="gray"
      onClick={() => {
        if (hasUsedVaultStorage) {
          dialogStore.trigger({
            component: VaultStorageDowngrade,
            props: {
              onContinue: () => {
                handleCancelSubscription();
              },
            },
          });
        } else {
          handleCancelSubscription();
        }
      }}
      radius="xl"
      loading={cancelingSubscription}
    >
      Proceed to Cancel
    </Button>
  );
};

export const CancelMembershipBenefitsModal = () => {
  const features = useFeatureFlags();
  const dialog = useDialogContext();
  const handleClose = dialog.onClose;
  const currentUser = useCurrentUser();
  const [mainBuzzType] = useAvailableBuzz();
  const { vault, isLoading: vaultLoading } = useQueryVault();
  const { subscription, subscriptionLoading, subscriptionPaymentProvider } = useActiveSubscription({
    buzzType: mainBuzzType,
    checkWhenInBadState: true,
  });
  const buzzConfig = useBuzzCurrencyConfig(mainBuzzType);
  const product = subscription?.product;
  const details = product ? getPlanDetails(product, features) : null;
  const benefits = details?.benefits ?? [];
  const hasUsedVaultStorage = !!vault && vault.usedStorageKb > 0;
  const isInCreatorProgram =
    !!currentUser && Flags.hasFlag(currentUser.onboarding, OnboardingSteps.CreatorProgram);

  return (
    <Modal
      {...dialog}
      size="md"
      title="You will lose the following if you cancel"
      radius="md"
      style={{
        '--buzz-color': buzzConfig.colorRgb,
        '--buzz-gradient': buzzConfig.css?.gradient,
      }}
    >
      {vaultLoading || subscriptionLoading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <Stack>
          {product && (
            <Paper withBorder radius="lg" p="lg">
              {benefits && (
                <PlanBenefitList
                  benefits={benefits}
                  buzzType={getSubscriptionDisplayBuzzType(subscription.buzzType, mainBuzzType)}
                  tier={subscription.product.metadata.tier}
                />
              )}
            </Paper>
          )}
          {isInCreatorProgram && (
            <AlertWithIcon
              align="center"
              color="red"
              size="sm"
              icon={<IconAlertTriangle size={28} />}
              iconColor="red"
              title="You're a Creator Program member"
            >
              Canceling ends your Creator Program membership: you won&rsquo;t be able to bank Buzz
              and your creator shop will be hidden until you renew. You can still withdraw any cash
              you&rsquo;ve already earned.
            </AlertWithIcon>
          )}
          <Group grow>
            {subscriptionPaymentProvider === PaymentProvider.Stripe && (
              <StripeCancelMembershipButton
                onClose={handleClose}
                hasUsedVaultStorage={hasUsedVaultStorage}
              />
            )}
            {subscriptionPaymentProvider === PaymentProvider.Paddle && (
              <PaddleCancelMembershipButton
                onClose={handleClose}
                hasUsedVaultStorage={hasUsedVaultStorage}
              />
            )}
            <Button color="blue" onClick={handleClose} radius="xl">
              Don&rsquo;t cancel plan
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};

export const VaultStorageDowngrade = ({
  continueLabel = 'Continue',
  cancelLabel = 'Go back',
  onContinue,
}: {
  onContinue: () => void;
  continueLabel?: string;
  cancelLabel?: string;
}) => {
  const features = useFeatureFlags();
  const dialog = useDialogContext();
  const handleClose = dialog.onClose;
  const { vault, isLoading: vaultLoading } = useQueryVault();
  const { items, isLoading: loadingVaultItems, pagination } = useQueryVaultItems();
  const { subscription, subscriptionLoading } = useActiveSubscription();
  const product = subscription?.product;
  const shownItems = items.filter((i) => !!i.coverImageUrl).slice(0, 3);

  return (
    <Modal {...dialog} size="md" title="Are you sure?" radius="md">
      {vaultLoading || subscriptionLoading || loadingVaultItems ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <Stack>
          <Group wrap="nowrap" justify="center">
            {shownItems.map((item) => (
              <Image
                key={item.id}
                src={item.coverImageUrl}
                alt="Model Image"
                radius="lg"
                w={100}
                h={100}
              />
            ))}
          </Group>
          <Stack gap={0}>
            <Text align="center">
              You have{' '}
              <Text component="span" fw="bold">
                {formatKBytes(vault?.usedStorageKb ?? 0)}
              </Text>{' '}
              of storage used and{' '}
              <Text component="span" fw="bold">
                {pagination?.totalItems ?? 0} models
              </Text>{' '}
              stored on your Vault. After this change, your available Vault storage may be reduced,
              and if the new storage limit is exceeded, your Vault will be frozen.
            </Text>
            <Text c="dimmed" align="center">
              You will have a 7 day grace period to download models from your Vault if it becomes
              frozen.
            </Text>
          </Stack>
          <Group grow>
            <Button
              color="gray"
              onClick={() => {
                onContinue();
                handleClose();
              }}
              radius="xl"
            >
              {continueLabel}
            </Button>
            <Button color="blue" onClick={handleClose} radius="xl">
              {cancelLabel}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};

export const MembershipUpgradeModal = ({
  priceId,
  meta,
  price,
}: {
  priceId: string;
  price: Partial<Price>;
  meta: PlanMeta;
}) => {
  const dialog = useDialogContext();
  const handleClose = dialog.onClose;
  const { name, image, benefits } = meta;

  // Capture an optional referral code on upgrade. Pre-fill from the
  // ref_code cookie (set by ?ref_code= landing) so users referred by a
  // friend don't have to remember the code on the upgrade flow.
  const { code: cookieRefCode } = useReferralsContext();
  const [refCodeInput, setRefCodeInput] = useState(cookieRefCode ?? '');
  // Open by default if a code is already in the cookie so users referred by
  // a friend see it pre-attached without hunting for the field.
  const [refCodeOpen, setRefCodeOpen] = useState(!!cookieRefCode);

  const queryUtils = trpc.useUtils();
  const currentUser = useCurrentUser();
  const { mutate: upgrade, isPending: upgrading } =
    trpc.stripe.createSubscriptionSession.useMutation({
      async onSuccess({ sessionId, url }) {
        await currentUser?.refresh();
        await queryUtils.subscriptions.getUserSubscription.reset();
        handleClose();
        if (url) Router.push(url);
        else if (sessionId) {
          const stripe = await getClientStripe();
          if (!stripe) return;
          await stripe.redirectToCheckout({ sessionId });
        }
      },
      onError(error) {
        showErrorNotification({
          title: 'Sorry, there was an error while trying to upgrade. Please try again later',
          error: new Error(error.message),
        });
      },
    });

  const submit = () => {
    const trimmed = refCodeInput.trim();
    upgrade({ priceId, refCode: trimmed.length > 0 ? trimmed : undefined });
  };

  return (
    <Modal
      {...dialog}
      size="md"
      title={`You are about to upgrade to the ${name} plan!`}
      radius="md"
    >
      <Stack>
        {image && (
          <Center>
            <Box w={120}>
              <EdgeMedia src={image} />
            </Box>
          </Center>
        )}

        {price?.interval === 'year' && (
          <Center>
            <Badge variant="filled" color="green">
              Annual Plan
            </Badge>
          </Center>
        )}

        {benefits && (
          <Paper className="h-full rounded-md bg-gray-0 p-5 dark:bg-dark-8" withBorder>
            <PlanBenefitList benefits={benefits} buzzType={meta.buzzType} tier={meta.tier} />
          </Paper>
        )}

        <Stack gap="xs">
          <UnstyledButton
            onClick={() => setRefCodeOpen((v) => !v)}
            className="w-full"
            aria-expanded={refCodeOpen}
            disabled={upgrading}
          >
            <Group gap={6} justify="space-between" wrap="nowrap">
              <Text size="sm" c="blue.4" td="underline">
                Have a referral code?
              </Text>
              {refCodeOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
            </Group>
          </UnstyledButton>
          <Collapse in={refCodeOpen}>
            <TextInput
              description="Add a friend's code so they earn referral perks on this upgrade — and you get bonus Blue Buzz."
              placeholder="ABCD1234"
              value={refCodeInput}
              onChange={(e) => setRefCodeInput(e.currentTarget.value.toUpperCase())}
              maxLength={32}
              disabled={upgrading}
            />
          </Collapse>
        </Stack>

        <Alert color="orange">
          <Stack>
            <Text>
              Please note there will be up to{' '}
              <Text component="span" fw="bold">
                an hour
              </Text>{' '}
              delay from when you upgrade to when you receive your Buzz &amp; get charged. All other
              membership perks will be immediate.
            </Text>
            {price.interval === 'year' && (
              <Text>
                <Text className="font-bold" component="span">
                  Important:
                </Text>{' '}
                For yearly plans, Buzz will still be distributed monthly. If you have an active
                yearly subscription, the remainder of your subscription will be applied as a
                discount.
              </Text>
            )}
          </Stack>
        </Alert>

        <Group grow>
          <Button color="blue" onClick={submit} radius="xl" loading={upgrading}>
            Upgrade now
          </Button>
          <Button color="gray" onClick={handleClose} radius="xl" disabled={upgrading}>
            Don&rsquo;t change plan
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
